import { useSelector } from 'react-redux';
import { Collapse, Flex, MediaQuery } from '@mantine/core';
import { useIntl } from 'react-intl';
import { IoChevronBackSharp } from 'react-icons/io5';
import { useParams, useSearchParams } from 'react-router-dom';
import { getLangState } from '../../../../../store/lang.slice';
import { IoArrowBackSharp } from 'react-icons/io5';
import { getSettingsState } from '../../../../../store/settings.slice';
import { useState, useEffect } from 'react';
import { ReactComponent as Arrow } from '../../../../../assets/images/svg/angle-right-small.svg';
import { FaRegUser } from 'react-icons/fa6';
import { LuClock } from 'react-icons/lu';
import { MdOutlineCalendarToday } from 'react-icons/md';
import { GiWorld } from 'react-icons/gi';
import { FiMapPin } from 'react-icons/fi';
import { AiOutlineVideoCamera } from 'react-icons/ai';
import { TbPhoneCall } from 'react-icons/tb';
import { ReactComponent as SaudiRiyalSymbol } from '../../../../../assets/images/svg/Saudi_Riyal_Symbol-2.svg';

import dayjs from 'dayjs';
import { convertTimeFormatTo12 } from '@jadwalh/utils';
import { useDisclosure, useMediaQuery } from '@mantine/hooks';
import {
  normalizeCurrencyCode,
  resolveCurrencyDisplayLabel,
} from '../../../../../utils/formatPaymentAmount';

export default function EventInfo({
  formStep,
  reschedule,
  backAction,
  date,
  time,
  timeTo,
  servedAt,
  served_at_value,
  price,
  host,
  timeZone,
  newTimeZoneName,
  isLastStep,
  title,
  description,
  duration,
  hide_duration,
  oldAppointment,
  served_at_data,
  selectedSlots,
  isMeetingPoll = false,
  currency_id,
  currency_label,
}: any) {
  const [showHeader, setShowHeader] = useState(false);
  const langType = useSelector(getLangState);
  const settings = useSelector(getSettingsState);
  const { formatMessage } = useIntl();
  const lang = useSelector(getLangState);
  const { tenantSlug, client, event } = useParams();
  const [searchParams] = useSearchParams();
  const teamSlug = searchParams.get('team');
  const [opened, { toggle }] = useDisclosure(false);

  useEffect(() => {
    setShowHeader(!!searchParams.get('fromList'));
  }, [searchParams]);

  const isMobile = useMediaQuery('(max-width: 450px)');

  const numericPrice = Number(price);
  const shouldShowPrice = Number.isFinite(numericPrice) && numericPrice > 0;
  const normalizedCurrencyCode = normalizeCurrencyCode(currency_id) ?? 'SAR';
  const shouldUseSarSymbol = normalizedCurrencyCode === 'SAR';
  const formattedPriceAmount = shouldShowPrice
    ? numericPrice.toLocaleString(lang === 'ar' ? 'ar' : 'en-US', {
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
      })
    : null;
  const priceCurrencyLabel =
    shouldShowPrice &&
    !shouldUseSarSymbol &&
    resolveCurrencyDisplayLabel({
      currencyLabel: currency_label,
      currencyCode: normalizedCurrencyCode,
      formatMessage,
    });

  return (
    <Flex
      direction={'column'}
      w="100%"
      className="infoWrapper"
      style={{
        position: 'relative',
        height: 'auto',
        maxHeight: '405px',
        overflow: 'auto',
        scrollbarWidth: 'thin',
        scrollbarColor: '#888 #f1f1f1',
      }}
    >
      {formStep === 1 && showHeader && (
        <Flex
          gap="sm"
          w="100%"
          align="flex-start"
          className="backBtn link"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();

            // If consumer passed a custom backAction, use it
            if (backAction) {
              backAction();
              return;
            }

            // Fallback: go back to the services list, preserving tenant slug if present
            // If we have a team slug from the URL, use it instead of client slug
            const targetSlug = teamSlug || client;
            const redirectUrl =
              tenantSlug && targetSlug
                ? `/${tenantSlug}/${targetSlug}`
                : targetSlug
                ? `/${targetSlug}`
                : null;

            if (redirectUrl) {
              window.location.href = redirectUrl;
            } else {
              window.history.back();
            }
          }}
        >
          <MediaQuery styles={{ display: 'none' }} largerThan="md">
            {langType === 'ar' ? <Arrow /> : <IoArrowBackSharp />}
          </MediaQuery>
          <span className="backIcon">
            {langType === 'ar' ? <Arrow /> : <IoChevronBackSharp />}
          </span>
          <span>{formatMessage({ id: 'back' })}</span>
        </Flex>
      )}

      <Flex gap="8px" direction={'column'}>
        {(formStep === 1 || (formStep === 2 && !isMobile)) && (
          <div className="containerServise">
            <h3 style={{ marginBottom: isLastStep ? '8px' : '0' }}>{title}</h3>
          </div>
        )}
        {formStep === 1 && !reschedule && (
          <p className="description" style={{ wordBreak: 'break-word' }}>
            <div dangerouslySetInnerHTML={{ __html: description }} />
          </p>
        )}
        <Flex direction={'column'} gap="12px">
          {(formStep === 1 || isLastStep) && host && (
            <Flex
              gap="7px"
              justify="flex-start"
              align="center"
              direction="row"
              wrap="nowrap"
              className="meeting-info"
            >
              <FaRegUser width="16px" />
              <p className="duration">{host}</p>
            </Flex>
          )}
          {formStep === 1 && hide_duration === 0 && duration && (
            <Flex
              gap="7px"
              justify="flex-start"
              align="center"
              direction="row"
              wrap="nowrap"
              className="meeting-info"
            >
              <LuClock style={{ width: '16px' }} />
              <p className="duration">
                {duration} {formatMessage({ id: 'minute' })}
              </p>
            </Flex>
          )}

          {formStep === 1 && servedAt && (
            <Flex
              gap="7px"
              justify="flex-start"
              align="center"
              direction="row"
              wrap="nowrap"
              className="meeting-info"
            >
              {served_at_value === 0 && <TbPhoneCall size={16} />}
              {served_at_value === 1 && <TbPhoneCall size={16} />}
              {served_at_value === 2 && <FiMapPin size={16} />}
              {served_at_value === 3 && <AiOutlineVideoCamera size={16} />}
              {served_at_value === 4 && <AiOutlineVideoCamera size={16} />}
              <p className="duration">{servedAt}</p>
            </Flex>
          )}
          {isLastStep && served_at_data && (
            <Flex
              gap="7px"
              justify="flex-start"
              align="center"
              direction="row"
              wrap="nowrap"
              className="meeting-info"
            >
              {served_at_value === 0 && <TbPhoneCall size={16} />}
              {served_at_value === 1 && <TbPhoneCall size={16} />}
              {served_at_value === 2 && <FiMapPin size={16} />}
              {served_at_value === 3 && <AiOutlineVideoCamera size={16} />}
              {served_at_value === 4 && <AiOutlineVideoCamera size={16} />}
              <p
                className="duration"
                style={
                  served_at_value === 1 || served_at_value === 0
                    ? { direction: 'ltr' }
                    : {}
                }
              >
                {served_at_data}
              </p>
            </Flex>
          )}

          {(formStep === 2 || isLastStep) && oldAppointment && (
            <>
              <Flex
                gap="7px"
                justify="flex-start"
                align="center"
                direction="row"
                wrap="nowrap"
                className="meeting-info"
              >
                <MdOutlineCalendarToday size={16} />
                <Flex direction={'column'} gap="2px">
                  <Flex gap={'2px'}>
                    <p className="duration">
                      {formatMessage({ id: 'previous' })}:{' '}
                    </p>
                    <p
                      className="duration"
                      style={{ textDecoration: 'line-through' }}
                    >
                      {dayjs(oldAppointment.date)
                        .locale(lang)
                        .format('dddd DD MMMM YYYY')}
                    </p>
                  </Flex>
                </Flex>
              </Flex>
              <Flex
                gap="7px"
                justify="flex-start"
                align="center"
                direction="row"
                wrap="nowrap"
                className="meeting-info"
              >
                <LuClock size={16} />
                <Flex direction={'column'} gap="2px">
                  <Flex gap={'2px'}>
                    <p className="duration">
                      {formatMessage({ id: 'previous' })}:{' '}
                    </p>
                    <p
                      className="duration"
                      style={{ textDecoration: 'line-through' }}
                    >
                      {oldAppointment &&
                        (isLastStep
                          ? `${convertTimeFormatTo12(
                              oldAppointment.time_from,
                              lang
                            )} - ${convertTimeFormatTo12(
                              oldAppointment.time_to,
                              lang
                            )}`
                          : convertTimeFormatTo12(
                              oldAppointment.time_from,
                              lang,
                              false
                            ))}
                    </p>
                  </Flex>
                </Flex>
              </Flex>
            </>
          )}

          {(formStep === 2 || isLastStep || reschedule) && date && time && (
            <>
              <Flex gap={'7px'} align={'center'}>
                <MdOutlineCalendarToday size={16} />
                <Flex
                  gap="7px"
                  justify="flex-start"
                  align="flex-start"
                  direction="column"
                  wrap="nowrap"
                  className="meeting-info"
                  id="date-info"
                >
                  <p className="duration">
                    {dayjs(date).locale(lang).format('dddd DD MMMM YYYY')}
                  </p>
                </Flex>
              </Flex>
              <Flex gap={'7px'} align={'center'}>
                <LuClock size={16} />
                <Flex
                  gap="7px"
                  justify="flex-start"
                  align="flex-start"
                  direction="column"
                  wrap="nowrap"
                  className="meeting-info"
                  id="date-info"
                >
                  {
                    <p className="duration">
                      {isLastStep
                        ? `${convertTimeFormatTo12(time, lang)} ${
                            isLastStep && timeTo
                              ? ' - ' + convertTimeFormatTo12(timeTo, lang)
                              : ''
                          }`
                        : convertTimeFormatTo12(time, lang, false)}
                    </p>
                  }
                </Flex>
              </Flex>
            </>
          )}

          {formStep === 2 &&
            !isMobile &&
            oldAppointment &&
            newTimeZoneName &&
            oldAppointment?.time_zone?.label !== newTimeZoneName && (
              <Flex
                gap="7px"
                justify="flex-start"
                align="center"
                direction="row"
                wrap="nowrap"
                className="meeting-info"
              >
                <GiWorld size={16} />
                <Flex gap="2px">
                  <p className="duration">
                    {formatMessage({ id: 'previous' })}:{' '}
                  </p>
                  <p
                    className="duration"
                    style={{ textDecoration: 'line-through' }}
                  >
                    {oldAppointment?.time_zone?.label?.split(' ')[0]}
                  </p>
                </Flex>
              </Flex>
            )}

          {isLastStep &&
            oldAppointment &&
            timeZone &&
            oldAppointment?.time_zone?.label !== timeZone && (
              <Flex
                gap="7px"
                justify="flex-start"
                align="center"
                direction="row"
                wrap="nowrap"
                className="meeting-info"
              >
                <GiWorld size={16} />
                <Flex gap="2px">
                  <p className="duration">
                    {formatMessage({ id: 'previous' })}:{' '}
                  </p>
                  <p
                    className="duration"
                    style={{ textDecoration: 'line-through' }}
                  >
                    {oldAppointment?.time_zone?.label?.split(' ')[0]}
                  </p>
                </Flex>
              </Flex>
            )}

          {(reschedule || isLastStep) && timeZone && (
            <Flex
              gap="7px"
              justify="flex-start"
              align="center"
              direction="row"
              wrap="nowrap"
              className="meeting-info"
            >
              <GiWorld size={16} />
              <p className="duration">{timeZone?.split(' ')[0]}</p>
            </Flex>
          )}

          {formStep === 2 && newTimeZoneName && !isMobile && (
            <Flex
              gap="7px"
              justify="flex-start"
              align="center"
              direction="row"
              wrap="nowrap"
              className="meeting-info"
            >
              <GiWorld size={16} />
              <p className="duration">{newTimeZoneName?.split(' ')[0]}</p>
            </Flex>
          )}

          {shouldShowPrice && (
            <Flex
              gap="7px"
              justify="flex-start"
              align="center"
              direction="row"
              wrap="nowrap"
              className="meeting-info"
            >
              <p
                className="duration price-value"
                dir="ltr"
                style={{
                  display: 'inline-flex',
                  alignItems: 'baseline',
                  gap: '4px',
                  flexWrap: 'wrap',
                }}
              >
                <span>{formattedPriceAmount}</span>
                {shouldUseSarSymbol && (
                  <SaudiRiyalSymbol
                    className="sar-symbol"
                    style={{
                      width: '12px',
                      height: '14px',
                      flex: '0 0 auto',
                    }}
                  />
                )}
                {!shouldUseSarSymbol && priceCurrencyLabel && (
                  <span
                    className="sar-symbol"
                    style={{
                      fontSize: '12px',
                      lineHeight: 1,
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {priceCurrencyLabel}
                  </span>
                )}
              </p>
            </Flex>
          )}
          <Flex
            gap="7px"
            justify="flex-start"
            align="flex-start"
            direction="column"
            wrap="nowrap"
            className="meeting-info"
          >
            {selectedSlots &&
              Object.entries(selectedSlots).length > 0 &&
              formStep === 2 &&
              isMeetingPoll && (
                <>
                  <Flex
                    align="center"
                    justify="center"
                    gap="7px"
                    onClick={toggle}
                  >
                    <MdOutlineCalendarToday style={{ width: '16px' }} />
                    <p className="duration">
                      {Object.values(selectedSlots).reduce(
                        (total: number, times: any) =>
                          total + (Array.isArray(times) ? times.length : 0),
                        0
                      )}{' '}
                      {formatMessage({ id: 'preferredTime' })}
                    </p>
                  </Flex>
                  <Collapse
                    in={opened}
                    transitionDuration={1000}
                    transitionTimingFunction="linear"
                  >
                    {selectedSlots &&
                      Object.entries(selectedSlots).map(
                        ([date, times]: [string, any]) => (
                          <Flex
                            key={date}
                            direction="column"
                            gap="4px"
                            style={{ marginBottom: '12px' }}
                            pr="16px"
                          >
                            <p
                              className="duration"
                              style={{
                                fontWeight: 500,
                                marginBottom: '1px',
                                fontSize: '12px',
                              }}
                            >
                              {date}
                            </p>
                            <p style={{ fontWeight: 400, fontSize: '12px' }}>
                              {Array.isArray(times) &&
                                times
                                  .filter(
                                    (slot: { id: number | string }) =>
                                      slot.id !== undefined
                                  )
                                  .map(
                                    (slot: {
                                      time: string;
                                      id: number | string;
                                    }) => slot.time
                                  )
                                  .join(', ')}
                            </p>
                          </Flex>
                        )
                      )}
                  </Collapse>
                </>
              )}
          </Flex>
        </Flex>
      </Flex>
    </Flex>
  );
}
